import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../components/ElipsisText";
import Link from "../../../../components/Link";
import Badge from "../../../../components/Badge";
import Button from "../../../../components/Button";
import { Tooltip } from "@mui/material";
import { RowClassName, Modules, RouteParamsTypes, PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import { TransactionStatus } from "../../../../interfaces/transaction.interface"
import { TableContext } from "../../../../contexts/TableContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING, RESERVATION_STATUS } from "../../../../utils/appConstant";
import { hasResourseAccess, formatDollarAmount } from "../../../../utils/methods";

interface RowProps {
  item: any;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Reservation]: [PermissionActionsEnum.Update] }] });

  const handleViewClick = (id: string) => {
    navigate(`${Modules.reservations}${RouteParamsTypes.view}/${id}`);
  };

  const handleEditClick = (id: string) => {
    navigate(`${Modules.reservations}${RouteParamsTypes.edit}/${id}`);
  };

  const getStatusVariant = (status: TransactionStatus) => {
    if (status === VALIDATION_FIELD_MAPPING.unBilled) return "secondary";
    else if (status === VALIDATION_FIELD_MAPPING.invoice) return "warning"
    else return "info"
  }

  // Deliberately disjoint from getStatusVariant's palette (secondary/warning/info) so the
  // Statement Status badge is never mistaken for the Invoice Status badge in the same row.
  const getStatementStatusVariant = (status: string) => {
    if (status === 'pending') return "primary";
    else if (status === 'settled') return "success";
    else return "danger"; // generated
  }

  const propertyNode = (
    <div className="d-flex align-items-center">
      <div className="me-2" style={{ width: 34 }}>
        <img width={32} height={32} src={`/assets/images/${item?.platform?.platform_name}.svg`} />
      </div>
      <div style={{ width: 'calc(100% - 44px)' }}>
        <ElipsisText
          width="100%"
          text={item?.property?.name || "-"}
          node={
            <Link 
              text={item?.property?.name || "-"} 
              href={`${Modules.reservations}${RouteParamsTypes.view}/${item.id}`}
              onClick={() => handleViewClick(item.id)}
            />
          }
          className="h6 mb-1 text-decoration-underline hyperlink"
        />
        <ElipsisText width="100%" text={item?.property?.city_data?.name || '-'} />
      </div>
    </div>
  )

  const checkInDetailsNode = (
    <div>
      <ElipsisText width="100%" text={`${item?.check_in_date_list ?? '-'} - ${item?.check_out_date_list ?? '-'}`} className="h6 mb-1" />
      <ElipsisText width="100%" text={t("reservations.list.pageTexts.table.totalNights", { totalNights: item?.total_nights ?? "-" })} />
    </div>
  )

  const guestNode = (
    <div>
      <ElipsisText width="100%" text={item.guest_name || '-'} className="h6 mb-1" />
      <ElipsisText width="100%" text={t("reservations.list.pageTexts.table.totalGuests", { totalGuests: item.total_guests || "-" })} />
    </div>
  )

  const revenueNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.formatted_revenue ? formatDollarAmount(item.formatted_revenue) : '-'}</span>
      {!item.reviewed && (
        <Tooltip
          title={t("reservations.list.pageTexts.table.reviewedBy", { reviewedBy: item.reviewed_by_user?.full_name || "-" })}
          arrow
          classes={{ tooltip: "tooltip" }}
        >
          <i className="ri-spam-line text-warning ri-20px" />
        </Tooltip>
      )}
    </div>
  );

  const actionsNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      {
        hasEditAccess && (item.status != VALIDATION_FIELD_MAPPING.settled) ? (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-edit-box-line ri-20px"></i>}
            onClick={() => handleEditClick(item.id)}
          />
        ) : (
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-eye-line ri-20px"></i>}
            onClick={() => handleViewClick(item.id)}
          />
        )
      }
      <Tooltip
        title={`${t('reservations.list.pageTexts.table.lastSync')} ${item?.last_sync_with_hospitable ?? '-'}`}
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i className="ri-timer-line ri-20px"></i>
      </Tooltip>
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.property) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={propertyNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.checkInDetails) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={checkInDetailsNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.guest) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={guestNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.reservationStatus) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              className={clsx((item?.reservation_status === RESERVATION_STATUS.cancelled || item?.reservation_status === RESERVATION_STATUS.expired) && "text-danger")}
              text={item?.reservation_status_name ?? '-'}
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.hostPayout) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={revenueNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Badge
                  text={item.status_name || "-"}
                  variant={getStatusVariant(item.status)}
                />
              }
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementStatus) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Badge
                  text={item.statement_status_name || "-"}
                  variant={getStatementStatusVariant(item.statement_status)}
                />
              }
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.invoiceNumber) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.invoice?.invoice_no ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.statementNumber) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.manager_statement?.statement_number || '-'}
              node={item?.manager_statement ? (
                <Link
                  text={item.manager_statement.statement_number}
                  href={`${Modules.reconciliation}${RouteParamsTypes.managerStatement}/${item.manager_statement.id}`}
                  target="_blank"
                />
              ) : undefined}
              className={item?.manager_statement ? "text-decoration-underline hyperlink" : ""}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.source) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.source_name ?? '-'}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>{actionsNode}</td>
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? "-"} />);
      })}
    </tr>
  );
};

export default Row;
